import { decode } from 'he';
import Head from 'next/head';
import React, { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { getRentals, goToRentals } from '@/actions/rentals';
import MaxWidth from '@/components/common/layout/MaxWidth';
import NarrowWidth from '@/components/common/layout/NarrowWidth';
import LYRVBanner from '@/components/common/LYRV';
import { RentalsInlineList } from '@/components/common/RentalsList/RentalsInlineList';
import { BlogMenu } from '@/components/route/blog/BlogMenu';
import BlogPaginationCarousel from '@/components/route/blog/BlogPaginationCarousel';
import BlogPostHero from '@/components/route/blog/BlogPostHero/BlogPostHero';
import CategoryPosts from '@/components/route/blog/CategoryPosts';
import CategoryTitleLink from '@/components/route/blog/CategoryTitleLink';
import RentalRigs from '@/components/route/blog/RentalRigs';
import Heading from '@/components/switchback/Heading/Heading';
import NewsLetterSignup from '@/components/switchback/NewsLetterSignup/NewsLetterSignup';
import { EListingSource } from '@/services/analytics/listings/types';
import { trackEvent } from '@/services/track-event';
import { IBlogPost } from '@/services/types/blog/post.types';

interface IBlogCategoryPageTemplateProps {
  categorySlug: number;
  currentPage: number;
  posts: IBlogPost[];
  title: string;
  totalPages: number;
}

const BlogCategoryPageTemplate: React.FC<IBlogCategoryPageTemplateProps> = ({
  categorySlug,
  currentPage,
  title,
  totalPages,
  posts = [],
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const getPageRentals = useCallback((filters: any) => dispatch(getRentals(filters)), [dispatch]);

  useEffect(() => {
    getPageRentals({ count: 5 });

    trackEvent({
      event: 'Blog : Viewed page',
      pageType: 'Blog category page',
    });
  }, [getPageRentals]);
  return (
    <>
      <Head>
        <title>{title ? `${decode(String(title))} | Outdoorsy.com` : 'Outdoorsy'}</title>
        <link
          rel="canonical"
          href={`https://www.outdoorsy.com/blog/category/${categorySlug}/page/${currentPage}`}
        />

        {/* Need to get SEO description field without using Yoast */}
        <meta name="description" content={title} />

        <meta property="fb:app_id" content="604465049688341" />
        <meta name="msvalidate.01" content="1DD3784B3E7F8CD7B4C595940717F938" />
        <meta name="apple-itunes-app" content="app-id=1443835927, affiliate-data=1010lvba" />
        <meta
          name="google-site-verification"
          content="DuUf3p3Pr3-gmZfpMIg3gyG75gl7C3JpXWb0-ilQJbI"
        />

        {intl.locale === 'en-us' ? (
          <meta name="robots" content="index, follow, max-image-preview:large" />
        ) : (
          <meta name="robots" content="noindex" />
        )}

        <meta
          name="googlebot"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />
        <meta
          name="bingbot"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />

        <meta property="og:locale" content="en_US" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${title} | Outdoorsy.com`} />
        <meta
          property="og:url"
          content={`https://www.outdoorsy.com/blog/${categorySlug}/page/${currentPage}`}
        />
        <meta property="og:site_name" content="Outdoorsy RV Blogs" />

        {/* Need to get SEO description field without using Yoast */}
        <meta property="og:description" content={title} />
        <meta name="twitter:card" content="summary" />
      </Head>
      <LYRVBanner />

      {posts[0] && <BlogPostHero post={posts[0]} />}

      <div className="relative z-50 mx-auto">
        <BlogMenu />
      </div>
      <hr />

      <NarrowWidth className="mt-12 lg:max-w-lg" collapseMobile>
        <CategoryTitleLink
          level={1}
          slug={String(categorySlug)}
          title={title}
          headingClassName="highlight autoType900"
        />
      </NarrowWidth>

      <NarrowWidth className="lg:max-w-lg">
        <CategoryPosts posts={posts.slice(1, 10)} />
      </NarrowWidth>

      <NarrowWidth className="mt-12 mb-8">
        <BlogPaginationCarousel
          categorySlug={String(categorySlug)}
          currentPage={currentPage}
          totalPages={totalPages}
        />
      </NarrowWidth>

      <MaxWidth className="m-auto mt-12">
        <hr />
        <Heading className="mx-4 my-8 font-medium lg:mx-0 text-800">
          {intl.formatMessage({ defaultMessage: 'Ready to get started.', id: 'EMpVYE' })}
        </Heading>
        <RentalsInlineList
          goToRentals={() => dispatch(goToRentals())}
          eventSource={EListingSource.GRID_LAYOUT}
        />
      </MaxWidth>

      <MaxWidth collapseMobile className="m-auto my-12">
        <Heading className="mx-4 my-8 font-medium lg:mx-0 text-800">
          {intl.formatMessage({ defaultMessage: 'Travel the way you want it.', id: '7ndkZ9' })}
        </Heading>
        <RentalRigs />
      </MaxWidth>

      <div className="py-2 mt-4 bg-gray-100">
        <MaxWidth collapseMobile className="m-auto my-12">
          <NewsLetterSignup />
        </MaxWidth>
      </div>
    </>
  );
};

export default BlogCategoryPageTemplate;
